import { addDays, addMinutes } from 'date-fns';
import { OrderItem, UseOrdersData } from './order.types';
import {
    BookingStatus,
    GetBookingsResItem0Dto,
    GetBookingsResItem1Dto,
    GetOrdersResItemDto,
    OrderStatus,
} from '../../generated';
import { getBookingDtoToBooking } from '../booking/booking.converters';
import { convertNameDtoToFullName } from '../user/user.converters';
import {
    convertActivityDtoToActivity,
    convertDateToTimestamp,
    getStatusChangeReason,
} from '../common.converters';
import { PaymentStatus } from '../booking/booking.types';

export const getOrderItemDtoToOrder = (
    order: GetOrdersResItemDto
): OrderItem => ({
    id: order.id,
    number: order.number,
    noActiveBookings:
        order.bookings?.filter(b => b.status !== BookingStatus.CANCELLED)
            .length === 0,
    createdBy: convertNameDtoToFullName(order.created.by.name),
    participants: {
        customer: {
            id: order.customer.id,
            fullName: order.customer.name
                ? convertNameDtoToFullName(order.customer.name)
                : '',
            numbers:
                order.customer.contact_details?.phone_numbers?.map(number => ({
                    value: `${number.calling_code} ${number.number}`,
                })) || [],
            emails:
                order.customer.contact_details?.emails?.map(value => ({
                    value,
                })) || [],
        },
    },
    permissions: {
        canEdit: !!order.access.permissions?.update,
        canDelete: !!order.access.permissions?.delete,
    },
    financials: order.price
        ? {
              status: order.bookings?.some(
                  b =>
                      b.financials?.debt.original &&
                      b.status !== BookingStatus.CANCELLED
              )
                  ? PaymentStatus.NOT_PAID
                  : PaymentStatus.PAID,
              total: order.price.total || 0,
              subtotal: order.price.subtotal || 0,
              discount: order.price.discount,
          }
        : undefined,
    status: order.status,
    bookingsCount: order.summary?.counts.bookings || 0,
    productsCount: order.summary?.counts.products || 0,
    travelersCount: order.summary?.counts.customers || 0,
    reason:
        order.status === OrderStatus.CANCELLED
            ? getStatusChangeReason(order.lifecycle, OrderStatus.CANCELLED)
            : undefined,
    bookings:
        order.bookings?.map(v =>
            getBookingDtoToBooking(
                v as GetBookingsResItem0Dto | GetBookingsResItem1Dto
            )
        ) || [],
    lifecycle: order.lifecycle?.map(convertActivityDtoToActivity) || [],
});

export const orderFilterToRequestBodyConverter = (
    filters: Record<string, any>
): UseOrdersData => ({
    statuses: filters.statuses,
    text_search: filters.searchText || undefined,
    ...((!!filters.createdAt || !!filters.createdBy) && {
        action: 'created',
        at_start: filters.createdAt?.startDate
            ? convertDateToTimestamp(filters.createdAt.startDate, true)
            : undefined,
        at_end: filters.createdAt?.endDate
            ? convertDateToTimestamp(
                  addMinutes(addDays(filters.createdAt.endDate, 1), -1),
                  true
              )
            : undefined,
        by: filters.createdBy || undefined,
    }),
    ...((!!filters.updatedAt || !!filters.updatedBy) && {
        action: 'last_updated',
        at_start: filters.updatedAt?.startDate
            ? convertDateToTimestamp(filters.updatedAt.startDate, true)
            : undefined,
        at_end: filters.updatedAt?.endDate
            ? convertDateToTimestamp(
                  addMinutes(addDays(filters.updatedAt.endDate, 1), -1),
                  true
              )
            : undefined,
        by: filters.updatedBy || undefined,
    }),
    ...((!!filters.deletedAt || !!filters.deletedBy) && {
        action: 'deleted',
        at_start: filters.deletedAt?.startDate
            ? convertDateToTimestamp(filters.deletedAt.startDate, true)
            : undefined,
        at_end: filters.deletedAt?.endDate
            ? convertDateToTimestamp(
                  addMinutes(addDays(filters.deletedAt.endDate, 1), -1),
                  true
              )
            : undefined,
        by: filters.deletedBy || undefined,
    }),
});
